import type {
  Field,
  Process,
  ProcessProps,
  Step,
  Swimlane
} from "@certa/studio-core";
import { ComponentKey, getSlugifiedTag } from "@certa/studio-core";
import { compact } from "lodash-es";
import { useMemo } from "react";
import { dbOpsUtils } from "../hooks/useStudioAPI/hooks/dbOps.store";
import type { NodeLayoutData } from "../types/layout.types";
import type { ShallowMetaJSON } from "../types/shallowMap.types";
import { arrayToHashmap } from "@certa/common/utils/array.ts";
import { studioState } from "../hooks/useStudioAPI";

export const generateTag = (
  processId: string,
  node: NodeLayoutData
): string => {
  if (node.__tagReadonly) return node.tag;
  const generatedTag = getSlugifiedTag(node.name);

  /** Check if duplicate tag exists */
  const checkForDuplicateTag = (tag: string): string => {
    const swimlanesWithMatch = studioState().getSwimlaneByTag(processId, tag);
    const stepsWithMatch = studioState().getStepByTag(processId, tag);
    const fieldWithMatch = studioState().getFieldByTag(processId, tag);

    if (swimlanesWithMatch || stepsWithMatch || fieldWithMatch) {
      // Extract ending part after _ from tag if it exists
      const endingStr = tag.match(/_\d+$/)?.[0];
      const randomNum = Math.floor(Math.random() * 100000);
      let originalTag = tag;
      if (endingStr !== undefined) {
        originalTag = tag.substring(0, tag.length - endingStr.length);
      }
      return checkForDuplicateTag(`${originalTag}_${randomNum}`);
    } else {
      return tag;
    }
  };

  const duplicateCheckedTag = generatedTag
    ? checkForDuplicateTag(generatedTag)
    : "";

  return duplicateCheckedTag;
};

/**
 * Returns a MetaJSON object with all the child-nodes
 * of the component ID / Type specified in the parameters.
 * @param componentType
 * @param componentId
 * @returns
 */
export const getMetaJSONFromDB = ({
  processId,
  componentId,
  componentType,
  skipRules
}: {
  processId: string;
  componentId: string;
  componentType: ComponentKey;
  skipRules?: boolean;
}) => {
  try {
    let bulkActionIDs: string[] = [];
    let swimlaneIds: string[] = [];
    let stepIds: string[] = [];
    let fieldIds: string[] = [];
    let ruleIds: string[] = [];
    let conditionGroupIds: string[] = [];
    let conditionIds: string[] = [];
    let processIdToDupe = "";
    if (componentType === ComponentKey.SWIMLANES) {
      swimlaneIds = [componentId];
    } else if (componentType === ComponentKey.STEPS) {
      stepIds = [componentId];
    } else if (componentType === ComponentKey.FIELDS) {
      fieldIds = [componentId];
    } else if (componentType === ComponentKey.PROCESS) {
      processIdToDupe = componentId;
    }

    let process: ProcessProps | undefined;
    let bulkActions = {};
    let swimlanes = {};
    let steps = {};
    let fields = {};
    let rules = {};
    let conditionGroups = {};
    let conditions = {};

    // 1. Get the process (if applicable)
    if (processIdToDupe) {
      process = studioState().getProcess(processIdToDupe);
      swimlaneIds = [...(process?.swimlaneSequence || [])];
      bulkActionIDs = [...(process?.bulkActions || [])];
    }

    // 2. Get the bulk Actions (if applicable)
    if (bulkActionIDs.length) {
      const bulkActionsArray = studioState().getBulkActions(
        processId,
        bulkActionIDs
      );
      bulkActions = arrayToHashmap(bulkActionsArray, "id");
    }

    // 3. Get the swimlanes (if applicable)
    if (swimlaneIds.length) {
      const swimlanesArray = studioState().getSwimlanes(processId, swimlaneIds);

      swimlanes = arrayToHashmap(swimlanesArray, "id");

      swimlanesArray.forEach(swimlane => {
        stepIds = [...stepIds, ...(swimlane?.steps || [])];
        if (skipRules) return;
        ruleIds = [...ruleIds, ...getSwimlaneRuleIds(swimlane)];
      });
    }

    // 4. Get the steps (if applicable)
    if (stepIds.length) {
      const stepsArray = studioState().getSteps(processId, stepIds);

      steps = arrayToHashmap(stepsArray, "id");

      stepsArray.forEach(step => {
        if (step?.fields) {
          // TODO: There's bug where sometimes `step.fields` contains `null` value
          // and if a logic is iterating over that looking for `id` of that, they
          // get an error.
          // For a temporary fix we are filtering it right here but it's better if we
          // could get an RCA on this.
          step.fields = step.fields.filter(Boolean);
          fieldIds = [...fieldIds, ...step.fields];
        }
        if (skipRules) return;
        ruleIds = [...ruleIds, ...getStepRuleIds(step)];
      });
    }

    // 5. Get the fields (if applicable)
    if (fieldIds.length) {
      const fieldsArray = studioState().getFields(processId, fieldIds);

      fields = arrayToHashmap(fieldsArray, "id");
      if (!skipRules) {
        fieldsArray.forEach(field => {
          ruleIds = [...ruleIds, ...getFieldRuleIds(field)];
        });
      }
    }

    // 6. Get the rules (if applicable)
    if (ruleIds.length && !skipRules) {
      const rulesArray = studioState().getRules(processId, ruleIds);

      rules = arrayToHashmap(rulesArray, "id");

      rulesArray.forEach(rule => {
        if (rule?.topLevelGroupId) {
          conditionGroupIds = [...conditionGroupIds, rule?.topLevelGroupId];
        }
      });
    }

    // 7. Get all the remaining condition groups -- all levels (if applicable)
    while (conditionGroupIds.length > 0 && !skipRules) {
      const conditionGroupsArray = studioState().getConditionGroups(
        processId,
        conditionGroupIds
      );

      // clearing the array since we got all items within it
      conditionGroupIds = [];

      conditionGroups = {
        // preserve existing ones because this control structure's body
        // will iterate several times an
        ...conditionGroups,
        ...arrayToHashmap(conditionGroupsArray, "id")
      };

      for (const conditionGroup of conditionGroupsArray) {
        conditionGroupIds = [
          ...conditionGroupIds,
          ...(conditionGroup?.groups || [])
        ];
        conditionIds = [...conditionIds, ...(conditionGroup?.conditions || [])];
      }
    }

    // 7. Get all the conditions -- all levels (if applicable)
    if (conditionIds.length && !skipRules) {
      const conditionArray = studioState().getConditions(
        processId,
        conditionIds
      );
      conditions = arrayToHashmap(conditionArray, "id");
    }

    // Preparing the final Meta JSON chunk
    const componentMetaJSON: Partial<Process> = {
      process,
      bulkActions,
      swimlanes,
      steps,
      fields,
      rules,
      conditionGroups,
      conditions
    };

    return componentMetaJSON;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      "There was an error occurred while preparing MetaJSON of component from DB",
      e
    );
    return null;
  }
};

export const useGetShallowProcessMetaJSON = (processId: string) => {
  const process = dbOpsUtils.useDBProcess(processId);

  const swimlaneIds: string[] = useMemo(() => {
    return process?.swimlaneSequence || [];
  }, [process]);
  const swimlanes = dbOpsUtils.useDBSwimlanes(processId, swimlaneIds);

  const stepIds: string[] = useMemo(() => {
    return swimlanes.map(swimlane => swimlane?.steps).flat();
  }, [swimlanes]);
  const steps = dbOpsUtils.useDBSteps(processId, stepIds);

  const fieldIds: string[] = useMemo(() => {
    return steps.map(step => step?.fields).flat();
  }, [steps]);
  const fields = dbOpsUtils.useDBFields(processId, fieldIds);

  return useMemo(
    () => convertToShallowMetaJSON(process, swimlanes, steps, fields),
    [process, swimlanes, steps, fields]
  );
};

export const getShallowProcessMetaJSON = (
  processId: string
): ShallowMetaJSON => {
  if (!processId) {
    throw Error("Process ID is mandatory");
  }
  try {
    const process = studioState().getProcess(processId);

    if (!process) {
      throw Error("Process not found in local DB");
    }

    const swimlaneIds = process?.swimlaneSequence || [];
    const swimlanes = compact(
      studioState().getSwimlanes(processId, swimlaneIds)
    );

    const steps = studioState().getSteps(
      processId,
      swimlanes?.map(el => el?.steps || []).flat()
    );

    const fields = studioState().getFields(
      processId,
      steps?.map(el => el.fields).flat()
    );

    return convertToShallowMetaJSON(process, swimlanes, steps, fields);
  } catch (e) {
    return defaultShallowMetaJSON;
  }
};

const defaultShallowMetaJSON: ShallowMetaJSON = {
  process: null,
  swimlanes: [],
  steps: [],
  fields: [],
  ruleIds: []
};

const convertToShallowMetaJSON = (
  process: ProcessProps | undefined,
  swimlanesDefs: Swimlane[],
  stepDefs: Step[],
  fieldDefs: Field[]
): ShallowMetaJSON => {
  if (!process) {
    return defaultShallowMetaJSON;
  }

  const swimlanes: ShallowMetaJSON["swimlanes"] = [];
  const steps: ShallowMetaJSON["steps"] = [];
  const fields: ShallowMetaJSON["fields"] = [];

  const stepIds: string[] = [];
  const fieldIds: string[] = [];
  const ruleIds: string[] = [];

  // Include ruleIds of Process
  ruleIds.push(...getProcessRuleIds(process));

  // Key = StepID
  const stepToSwimlaneMap: Record<
    string,
    {
      swimlaneId: string;
      swimlaneName: string;
    }
  > = {};

  // Key = fieldId
  const fieldToStepMap: Record<
    string,
    {
      stepId: string;
      stepName: string;
      swimlaneId: string;
      swimlaneName: string;
    }
  > = {};

  // 2. Get the swimlanes
  if (process.swimlaneSequence.length) {
    swimlanesDefs.forEach(swimlaneDef => {
      // .filter apparently doesn't make TS happy, so we're doing it
      // the long-way.
      if (!swimlaneDef) return;
      swimlanes.push(swimlaneDef);

      stepIds.push(...swimlaneDef.steps);
      ruleIds.push(...getSwimlaneRuleIds(swimlaneDef));

      swimlaneDef.steps.forEach(stepId => {
        stepToSwimlaneMap[stepId] = {
          swimlaneId: swimlaneDef.id,
          swimlaneName: swimlaneDef.name
        };
      });
    });
  }

  // 4. Get the steps (if applicable)
  if (stepIds.length) {
    stepDefs.forEach(step => {
      if (!step) return;
      steps.push({
        name: step.name,
        tag: step.tag,
        id: step.id,
        fields: step.fields,
        pdfConfig: step.pdfConfig,
        staticGroups: step.staticGroups,
        dependentSteps: step.dependentSteps,
        ...stepToSwimlaneMap[step.id]
      });
      fieldIds.push(...(step?.fields || []));

      ruleIds.push(...getStepRuleIds(step));

      step.fields.forEach(fieldId => {
        fieldToStepMap[fieldId] = {
          stepId: step.id,
          stepName: step.name,
          ...stepToSwimlaneMap[step.id]
        };
      });
    });
  }

  // 5. Get the fields (if applicable)
  if (fieldIds.length) {
    fieldDefs.forEach(field => {
      if (!field) return;

      fields.push({
        tag: field.tag,
        id: field.id,
        body: field.body,
        fieldType: field.fieldType,
        extra: field.extra,
        sourceMapping: field.sourceMapping,
        workflowMapping: field.workflowMapping,
        options: field.options,
        dataInheritanceConstraints: field.dataInheritanceConstraints,
        ...fieldToStepMap[field.id]
      });

      ruleIds.push(...getFieldRuleIds(field));
    });
  }

  return {
    process,
    swimlanes,
    steps,
    fields,
    ruleIds
  };
};

const getProcessRuleIds = (process?: ProcessProps) => [
  ...(process?.accessGroupConstraints || [])
];

const getSwimlaneRuleIds = (swimlane?: Swimlane) => [
  ...(swimlane?.accessGroupConstraints || [])
];

const getStepRuleIds = (step?: Step) => [
  ...(step?.accessGroupConstraints || []),
  ...(step?.pdfConstraints || []),
  ...(step?.stepActionConstraints || []),
  ...(step?.notifications || []),
  ...(step?.visibilityConstraints || []),
  ...(step?.versionNamingConstraints || [])
];

const getFieldRuleIds = (field?: Field) => [
  ...(field?.attachmentInheritanceConstraints || []),
  ...(field?.attachmentInheritanceConstraintsV2 || []),
  ...(field?.relatedWorkflowFileConstraintsV2 || []),
  ...(field?.childWorkflowCreationConstraints || []),
  ...(field?.visibilityConstraints || []),
  ...(field?.dataInheritanceConstraints || []),
  ...(field?.integrationPollingConstraints || []),
  ...(field?.integrationTriggerConstraints || []),
  ...(field?.dataReductionConstraints || []),
  ...(field?.relatedWorkflowFieldConstraints || []),
  ...(field?.RAGConstraints || []),
  ...(field?.readOnlyFieldConstraints || [])
];
